import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Avatar } from "../../design-system/Avatar";
import { GlobalSearchField } from "./GlobalSearchField";

/**
 * The toolbar above the route: search from the tablet tier up, plus the account
 * entry point on desktop.
 *
 * Deliberately a plain `<div>`, not a `<header>`. A second `<header>` alongside
 * the compact one would publish two `banner` landmarks, and the compact header
 * is only hidden by CSS -- it is still in the tree.
 *
 * Everything inside therefore has to carry its own landmark, or it counts as
 * content outside any region: the search is a `search` landmark and the account
 * link sits in its own labelled `nav`.
 *
 * There is no permanent connection indicator here. "Online" as a persistent
 * badge is noise; the state that matters -- offline, reconnecting -- is
 * announced by the banner region, which is a live region.
 *
 * The account link is named "Account", not "Settings". Two links with the same
 * accessible name pointing at the same route would be a genuine navigation
 * ambiguity for anyone listing links, and `app-shell.test.tsx` holds that line.
 */
export function ShellTopBar() {
  const { t } = useTranslation("foundation");

  return (
    <div className="shell-topbar">
      <GlobalSearchField className="shell-topbar__search" />
      <nav aria-label={t("shell.accountLabel")} className="shell-topbar__actions">
        <Link
          className="shell-account-link"
          to="/settings"
          activeProps={{ className: "shell-account-link shell-account-link--active" }}
        >
          <Avatar name={t("shell.accountLabel")} size="small" />
          <span>{t("shell.accountLabel")}</span>
        </Link>
      </nav>
    </div>
  );
}
